import { ChevronDown, ChevronRight } from "@carbon/icons-react";
import cx from "classnames";
import React, { type Ref } from "react";

import { type ExpandedFieldStateManager } from "../traverseValue/ExpandedFieldStateManager";
import { type ValuePath } from "../traverseValue/ValuePath";
import { classDoNotCollapseQueryBuilderOnClick } from "./hasClassDoNotCollapseQueryBuilderOnClick";
import { hasClassDoNotExpandObjectInspectorOnClick } from "./hasClassDoNotExpandOnClick";
import { type InspectorKeyNavigator } from "./useInspectorKeyNavigation";

export interface InspectorRowProps {
	children: React.ReactNode;
	navigator: InspectorKeyNavigator;
	prefix: ValuePath;
	fieldExpander: ExpandedFieldStateManager;
	showExpandButton?: boolean;
	ref: Ref<HTMLButtonElement>;
}

/**
 * Displays the expand/collapse button and indentation that precedes a value in the object
 * inspector. For example, an object will be displayed as follows, and this component is responsible
 * for rendering the caret `>` and the space before the actual value.
 *
 *     v myCell = {
 *         a: "b",
 *     >   c: { d: "e" },
 *       }
 */
const _InspectorRow = (
	{ children, navigator, prefix, fieldExpander, ...rest }: InspectorRowProps,
	buttonRef: React.ForwardedRef<HTMLButtonElement>
) => {
	const indentLevel = prefix.length - 1;
	const spacing = "\u00A0\u00A0".repeat(indentLevel); // Unicode version of `&nbsp;`.
	const showExpandButton = rest.showExpandButton === undefined ? true : rest.showExpandButton;

	const expanded = fieldExpander.expanded(prefix);

	const keydown = React.useCallback(
		(e: React.KeyboardEvent<HTMLButtonElement>) => {
			// Use keydown because it keypress does not register for arrow keys.
			switch (e.key) {
				case "ArrowRight":
					fieldExpander.expand(prefix);
					e.preventDefault();
					break;
				case "ArrowLeft":
					if (fieldExpander.expanded(prefix)) {
						fieldExpander.collapse(prefix);
					} else {
						navigator.focusButtonOfParent();
					}

					e.preventDefault();
					break;

				case "ArrowUp":
					navigator.focusPreviousButton();
					e.preventDefault();
					break;
				case "ArrowDown":
					navigator.focusNextButton();
					e.preventDefault();
					break;
			}
		},
		[fieldExpander, navigator, prefix]
	);

	const expandButton = (
		<button
			className="hover:bg-button-normal-tertiary-hover active:bg-button-normal-tertiary-active focus:shadow-border-focus focus:ring-default border-primary mr-2 w-4 rounded focus:outline-none focus:ring-4"
			ref={buttonRef}
			onClick={() =>
				expanded ? fieldExpander.collapse(prefix) : fieldExpander.expand(prefix)
			}
			data-testid="value-expand-button"
			onKeyDown={keydown}
		>
			{expanded ? (
				<ChevronDown className="fill-icon-default" size={16} />
			) : (
				<ChevronRight className="fill-icon-default" size={16} />
			)}
		</button>
	);

	// Disable a11y accessibility errors. We're making this UI accessible in other ways:
	//
	//   * Expand and collapse can be accomplished in the UI using the buttons on the lefthand side.
	//   * Expand/collapse button is navigable using both tab and the up/down arrows.
	//   * Clicking on the content expands/collapses the row, but also focuses the expand/collapse
	//     button for that row, providing both a visual indicator subsequently allowing key nav.
	const content = (
		// eslint-disable-next-line jsx-a11y/no-static-element-interactions,jsx-a11y/click-events-have-key-events
		<div
			onClick={(e) => {
				const shouldExpandOnClick =
					e.target instanceof Element &&
					!hasClassDoNotExpandObjectInspectorOnClick(e.target);
				if (expanded && shouldExpandOnClick) {
					fieldExpander.collapse(prefix);
				} else if (shouldExpandOnClick) {
					fieldExpander.expand(prefix);
				}
				navigator.focusCurrentButton();
			}}
			className={cx(
				showExpandButton ? "cursor-pointer" : "",
				// IMPORTANT: If the width is > 0, then the `whitespace-nowrap` in an ancestor
				// causes this to prevent dynamically shrinking the width of the row as a window
				// resizes.
				//
				// IF YOU DO CHANGE THIS: Check that window resizing works in the app and publish
				// templates. Make sure that code cells displaying the object expander grow and
				// shrink dynamically as window resizes, even when the expander is greater than the
				// row width.
				"w-0",
				"flex flex-row items-center"
			)}
		>
			{children}
		</div>
	);

	let indentElement = <div />;
	if (showExpandButton) {
		indentElement = expandButton;
	} else if (indentLevel > 0) {
		indentElement = <div className="mr-2 w-4 shrink-0" />;
	}

	return (
		<div
			className={cx(
				classDoNotCollapseQueryBuilderOnClick,
				`flex flex-row items-center font-mono`
			)}
			data-testid="inspector-row"
		>
			{indentElement}
			{spacing}
			{content}
		</div>
	);
};

// TODO: fix this type at some point -TW
// @ts-expect-error
export const InspectorRow = React.forwardRef(_InspectorRow);
